import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, User, X } from 'lucide-react';
import type { SubscriberRow } from '../Subscribers/types';
import { subscriberEmailStatus } from '../../lib/subscriberEmailStatus';
import { subscriberFunnelStatusLabel } from '../../lib/subscriberFunnelStatus';
import { subscriberSource, subscriberSourceLabel } from '../../lib/subscriberSource';
import { tierLabel } from '../../lib/personalization';
import {
  searchConnections,
  subscriberBestEmail,
  subscriberBestName,
  subscriberDetailPath,
  subscriberDisplayLabel,
} from '../../lib/subscriberDisplay';

type ConnectionHeaderSearchProps = {
  className?: string;
};

function connectionMeta(row: SubscriberRow): string {
  const parts: string[] = [];
  const role = typeof row.role === 'string' ? row.role.trim() : '';
  if (role) parts.push(role);
  const funnel = subscriberFunnelStatusLabel(row.funnel_status);
  if (funnel) parts.push(funnel);
  const emailStatus = subscriberEmailStatus(row);
  if (emailStatus) parts.push(emailStatus);
  const source = subscriberSource(row);
  if (source) parts.push(subscriberSourceLabel(source));
  return parts.join(' · ');
}

const ConnectionHeaderSearch: React.FC<ConnectionHeaderSearchProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SubscriberRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);

  const expanded = focused || query.trim().length > 0 || open;

  const resetDropdown = useCallback(() => {
    setResults([]);
    setError(null);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      resetDropdown();
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void searchConnections(q)
        .then((rows) => {
          setResults(rows);
          setActiveIndex(rows.length > 0 ? 0 : -1);
          setOpen(true);
        })
        .catch((e: unknown) => {
          setResults([]);
          setActiveIndex(-1);
          setError(e instanceof Error ? e.message : 'Search failed');
          setOpen(true);
        })
        .finally(() => setLoading(false));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [query, resetDropdown]);

  useEffect(() => {
    if (!open && !focused) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open, focused]);

  const collapseIfInactive = () => {
    window.setTimeout(() => {
      if (!rootRef.current?.contains(document.activeElement)) {
        setFocused(false);
        if (!query.trim()) setOpen(false);
      }
    }, 120);
  };

  const selectRow = (row: SubscriberRow) => {
    const path = subscriberDetailPath(row);
    if (!path) return;
    setQuery('');
    resetDropdown();
    setOpen(false);
    setFocused(false);
    navigate(path);
  };

  const showDropdown = open && query.trim().length >= 2;

  return (
    <div
      ref={rootRef}
      className={`relative shrink-0 transition-[width] duration-300 ease-in-out ${
        expanded ? 'w-[min(100%,28rem)]' : 'w-40 sm:w-48'
      } ${className}`.trim()}
    >
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setFocused(true);
            if (query.trim().length >= 2) setOpen(true);
          }}
          onBlur={collapseIfInactive}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              setActiveIndex(-1);
              setFocused(false);
              inputRef.current?.blur();
              return;
            }
            if (!showDropdown || results.length === 0) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => (i + 1) % results.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
            } else if (e.key === 'Enter' && activeIndex >= 0) {
              e.preventDefault();
              selectRow(results[activeIndex]);
            }
          }}
          placeholder="Search…"
          className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-shadow"
          aria-label="Search connections"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? listboxId : undefined}
          aria-autocomplete="list"
          role="combobox"
        />
        {loading ? (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        ) : query ? (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              resetDropdown();
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Clear connection search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50 max-h-80 overflow-y-auto"
        >
          {error ? (
            <p className="px-3 py-2 text-sm text-rose-600 dark:text-rose-400">{error}</p>
          ) : results.length === 0 && !loading ? (
            <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No connections found</p>
          ) : (
            <ul id={listboxId} role="listbox" aria-label="Connection results">
              {results.map((row, index) => {
                const name = subscriberBestName(row);
                const email = subscriberBestEmail(row);
                const meta = connectionMeta(row);
                const active = index === activeIndex;
                return (
                  <li key={String(row.id ?? email)} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectRow(row)}
                      className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${
                        active
                          ? 'bg-emerald-50 dark:bg-emerald-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="mt-0.5 shrink-0 rounded-full bg-gray-100 dark:bg-gray-700 p-1.5">
                        <User className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate min-w-0">
                            {subscriberDisplayLabel(row)}
                          </div>
                          {row.profile_score != null ? (
                            <span
                              className="shrink-0 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                              title={
                                row.profile_score_tier
                                  ? `Profile score · ${tierLabel(row.profile_score_tier)}`
                                  : 'Profile score'
                              }
                            >
                              {row.profile_score}
                            </span>
                          ) : null}
                        </div>
                        {email && name ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{email}</div>
                        ) : null}
                        {meta ? (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">{meta}</div>
                        ) : null}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectionHeaderSearch;
